/**
 * Status utilities - Single source of truth for photo status display logic.
 *
 * V2 Status Model:
 * - Backend stores "pending" status for all photos awaiting human review
 * - Frontend splits "pending" into display states based on has_unlabeled_faces:
 *   - "tagging": has_unlabeled_faces = true (needs face labeling)
 *   - "review": has_unlabeled_faces = false (ready for approval)
 *
 * This ensures consistent status display across Overview and Edit pages.
 */

// V2 Status Colors - complete mapping for all statuses
export const statusColors = {
  // Pipeline states (detecting/identifying deprecated - all use processing now)
  ingested: '#b8b8b8',
  processing: '#a29bff',
  detecting: '#a29bff', // DEPRECATED: maps to processing
  identifying: '#a29bff', // DEPRECATED: maps to processing
  // Review states (pending sub-states)
  tagging: '#ff9b64', // Orange - needs face tagging
  review: '#66d9e8', // Teal - ready for approval
  pending: '#ff9b64', // Fallback for pending (same as tagging)
  attention: '#66b7ff',
  skipped: '#888888',
  sent_to_editor: '#d4a5ff',
  recropping: '#d4a5ff',
  // Complete states
  done: '#68ff9a',
  exported: '#a29bff',
  archived: '#888888',
  // Export in progress states
  exporting: '#d4a5ff', // Purple (same as recropping)
  export_failed: '#ff6b6b', // Red (same as error)
  // Error state
  error: '#ff6b6b',
};

// Status labels for display
export const statusLabels = {
  ingested: 'Ingested',
  processing: 'Processing',
  detecting: 'Processing', // DEPRECATED: shown as Processing
  identifying: 'Processing', // DEPRECATED: shown as Processing
  tagging: 'Tagging',
  review: 'Review',
  pending: 'Pending',
  attention: 'Attention',
  skipped: 'Skipped',
  sent_to_editor: 'To Editor',
  recropping: 'Recropping',
  done: 'Done',
  exported: 'Exported',
  archived: 'Archived',
  exporting: 'Exporting',
  export_failed: 'Export Failed',
  error: 'Error',
};

// Status tooltips for hover text
export const statusTooltips = {
  ingested: 'Photo ingested, waiting for processing.',
  processing: 'Photo is being processed by the pipeline.',
  detecting: 'Photo is being processed by the pipeline.', // DEPRECATED
  identifying: 'Photo is being processed by the pipeline.', // DEPRECATED
  tagging: 'Needs face tagging.',
  review: 'Ready for approval.',
  pending: 'Ready for human review.',
  attention: 'Needs operator attention (e.g., no faces detected).',
  skipped: 'Photo skipped by operator.',
  sent_to_editor: 'Sent to external editor.',
  recropping: 'Manual recrop in progress.',
  done: 'Photo approved and complete.',
  exported: 'Photo exported.',
  archived: 'Photo archived.',
  exporting: 'Photo exports in progress.',
  export_failed: 'One or more exports failed.',
  error: 'Error occurred during processing.',
};

/**
 * Get the display status for a photo, splitting "pending" into sub-states.
 * @param {Object} photo - Photo object with status, has_unlabeled_faces, and export_summary
 * @returns {string} Display status key
 */
export function getDisplayStatus(photo) {
  if (!photo?.status) return 'pending';

  // Show "exporting" when done but exports still pending
  if (photo.status === 'done' && photo.export_summary) {
    const { pending_count, failed_count } = photo.export_summary;
    if (pending_count > 0) return 'exporting';
    if (failed_count > 0) return 'export_failed';
  }

  // Split "pending" into "tagging" vs "review" based on unlabeled faces
  if (photo.status === 'pending') {
    return photo.has_unlabeled_faces ? 'tagging' : 'review';
  }

  return photo.status;
}

/**
 * Get status label for display.
 * @param {Object} photo - Photo object
 * @returns {string} Human-readable status label
 */
export function getStatusLabel(photo) {
  const displayStatus = getDisplayStatus(photo);
  return statusLabels[displayStatus] || displayStatus.replace(/_/g, ' ');
}

/**
 * Get status color for display.
 * @param {Object} photo - Photo object
 * @returns {string} Hex color code
 */
export function getStatusColor(photo) {
  const displayStatus = getDisplayStatus(photo);
  return statusColors[displayStatus] || statusColors.pending;
}

/**
 * Get status tooltip for display.
 * @param {Object} photo - Photo object
 * @returns {string} Tooltip text
 */
export function getStatusTooltip(photo) {
  const displayStatus = getDisplayStatus(photo);
  return statusTooltips[displayStatus] || '';
}

/**
 * Map a photo's display status to one of the six redesign StatusPill kinds:
 * pending / attention / done / exported / error / skipped.
 *
 * Source: 01_Projects/Tank Redesign/UX_UI_GUIDE.md § StatusPill (six allowed
 * kinds; no invented states). The legacy status set has more granularity than
 * the redesign palette intentionally; we collapse here.
 */
/**
 * Overview vocabulary — 7 tiles (Total · Done · Review · Tagging ·
 * Attention · Skipped · Processing) used by StatsPills, plus an extra
 * `error` card-pill key for backend `error`/`export_failed` photos.
 *
 * Error photos count under the Attention tile (operator-relevant
 * umbrella) but render with red `Error` on the card so a real failure
 * still stands out. There's no separate Error tile — the umbrella
 * keeps the row to seven entries.
 */
export const OVERVIEW_TILE_LABEL = {
  done:       'Done',
  review:     'Review',
  tagging:    'Tagging',
  attention:  'Attention',
  error:      'Error',
  skipped:    'Skipped',
  processing: 'Processing',
};

/**
 * Map each tile key to a redesign StatusPill kind (palette only — the
 * label still comes from `OVERVIEW_TILE_LABEL`).
 *   done       → done     (phosphor)
 *   review     → exported (blue — closest to legacy review teal)
 *   tagging    → attention (amber)
 *   attention  → attention (amber)
 *   error      → error    (red — kept distinct for real failures)
 *   skipped    → skipped  (muted)
 *   processing → pending  (slate)
 */
export const OVERVIEW_TILE_KIND = {
  done:       'done',
  review:     'exported',
  tagging:    'tagging',
  attention:  'attention',
  error:      'error',
  skipped:    'skipped',
  processing: 'pending',
};

/**
 * Compute the per-photo card-pill key. Drives the card pill's label +
 * colour. Splits backend `attention` (amber) from `error`/`export_failed`
 * (red) so a real failure isn't drowned in routine attention noise.
 */
export function getOverviewTileKey(photo) {
  const display = getDisplayStatus(photo);
  switch (display) {
    case 'done':
    case 'exported':
    case 'archived':
    case 'exporting':
      return 'done';
    case 'review':
      return 'review';
    case 'tagging':
      return 'tagging';
    case 'attention':
      return 'attention';
    case 'error':
    case 'export_failed':
      return 'error';
    case 'skipped':
      return 'skipped';
    case 'ingested':
    case 'processing':
    case 'detecting':
    case 'identifying':
    case 'recropping':
    case 'sent_to_editor':
    case 'pending':
      return 'processing';
    default:
      return 'processing';
  }
}

export function getRedesignStatusKind(photo) {
  return OVERVIEW_TILE_KIND[getOverviewTileKey(photo)] || 'pending';
}

/**
 * Map UI filter keys to API status parameters.
 * Both "review" and "tagging" filters map to "pending" status on API,
 * with client-side filtering to split them.
 *
 * @param {string[]} activeFilters - Active UI filter keys
 * @returns {string[]} API status values (deduplicated)
 */
export function mapFiltersToApiStatus(activeFilters) {
  if (!activeFilters || activeFilters.length === 0) {
    return [];
  }

  const statusFilters = [];

  // Both review and tagging map to "pending" status
  if (activeFilters.includes('review')) statusFilters.push('pending');
  if (activeFilters.includes('tagging')) statusFilters.push('pending');

  // "problems" expands to attention + error
  if (activeFilters.includes('problems')) {
    statusFilters.push('attention');
    statusFilters.push('error');
  }

  // Direct status mappings
  if (activeFilters.includes('attention')) statusFilters.push('attention');
  // "done" filter includes both done and exported statuses
  if (activeFilters.includes('done')) {
    statusFilters.push('done');
    statusFilters.push('exported');
  }
  if (activeFilters.includes('sent_to_editor')) statusFilters.push('sent_to_editor');
  if (activeFilters.includes('exported')) statusFilters.push('exported');
  if (activeFilters.includes('error')) statusFilters.push('error');
  if (activeFilters.includes('skipped')) statusFilters.push('skipped');

  // Deduplicate (review + tagging both add "pending")
  return [...new Set(statusFilters)];
}

/**
 * Filter photos client-side to split "pending" into tagging/review.
 * Used after API returns all pending photos.
 *
 * @param {Object} photo - Photo object
 * @param {string[]} activeFilters - Active UI filter keys
 * @returns {boolean} Whether photo matches the active filters
 */
export function photoMatchesFilters(photo, activeFilters) {
  if (!activeFilters || activeFilters.length === 0) {
    return true; // No filters = show all
  }

  if (activeFilters.includes('all')) {
    return true;
  }

  const displayStatus = getDisplayStatus(photo);

  // Check if photo's display status matches any active filter
  for (const filter of activeFilters) {
    if (filter === 'review' && displayStatus === 'review') return true;
    if (filter === 'tagging' && displayStatus === 'tagging') return true;
    // "problems" matches attention or error status
    if (filter === 'problems' && (photo.status === 'attention' || photo.status === 'error'))
      return true;
    // "done" filter includes both done and exported statuses
    if (filter === 'done' && (photo.status === 'done' || photo.status === 'exported')) return true;
    if (filter === photo.status) return true;
  }

  return false;
}
